import { useId, useState, type FormEvent } from "react";
import { CheckCircle2 } from "lucide-react";
import { submitN8nForm } from "@/services/n8nService";

/** Every field the support workflow expects, filled in for a "tell me when Quilt is open to everyone" request (only the email comes from the visitor). */
function waitlistRequest(email: string) {
    return {
        type: "support" as const,
        source: "waitlist_no_provider",
        page: "/",
        user_role: "patient",
        name: "Quilt waitlist",
        email,
        phone: "0",
        clinic_name: "-",
        organization_name: "-",
        address: "-",
        zip_code: "0",
        topic: "Waitlist: notify when Quilt opens to everyone",
        message: `Someone without a provider asked to be told when Quilt is available to everyone (from quilt.gg). Please email ${email} when it's ready.`,
    };
}

/** Email-only waitlist for people without a clinic, sent through the same support form workflow as the rest of the site. */
export function NotifyMeForm() {
    const [email, setEmail] = useState("");
    const [state, setState] = useState<"idle" | "sending" | "done" | "error">("idle");
    const [error, setError] = useState("");
    const id = useId();

    const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setState("sending");
        // Hidden field that people never fill in; bots usually do
        if (new FormData(e.currentTarget).get("website")) return setState("done");
        const result = await submitN8nForm(waitlistRequest(email.trim().toLowerCase()));
        if (result.success) {
            setState("done");
        } else {
            setError(result.error || "Something went wrong. Please try again.");
            setState("error");
        }
    };

    if (state === "done") {
        return (
            <p role="status" className="mt-4 flex items-start gap-2.5 rounded-2xl bg-purple-50 p-3.5 text-sm text-slate-700 dark:bg-white/[0.04] dark:text-slate-300">
                <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-purple-600 dark:text-purple-400" aria-hidden="true" />
                <span>
                    <strong className="font-medium text-slate-900 dark:text-white">You're on the list.</strong> We'll email you when Quilt is ready.
                </span>
            </p>
        );
    }

    return (
        <form onSubmit={onSubmit} className="mt-4 max-w-md">
            <label htmlFor={`${id}-email`} className="mb-2 block text-sm font-medium text-slate-900 dark:text-white">
                Get notified when it's ready
            </label>
            <div className="flex items-center gap-1.5 rounded-full bg-white p-1.5 ring-1 ring-slate-300 focus-within:ring-2 focus-within:ring-purple-500 dark:bg-zinc-900 dark:ring-zinc-700">
                <input
                    id={`${id}-email`}
                    type="email"
                    name="email"
                    required
                    autoComplete="email"
                    inputMode="email"
                    placeholder="Your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    aria-describedby={`${id}-note`}
                    className="min-w-0 flex-1 bg-transparent pl-3 text-base text-slate-900 placeholder:text-slate-400 focus:outline-none dark:text-white dark:placeholder:text-slate-500"
                />
                <button
                    type="submit"
                    disabled={state === "sending"}
                    className="min-h-10 shrink-0 rounded-full bg-purple-600 px-4 text-sm font-medium text-white transition-colors hover:bg-purple-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 disabled:opacity-60 dark:bg-purple-500 dark:hover:bg-purple-400 dark:focus-visible:ring-offset-black"
                >
                    {state === "sending" ? "Sending…" : "Notify me"}
                </button>
            </div>
            {/* Honeypot: hidden from people and screen readers */}
            <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute -left-[9999px] h-px w-px opacity-0" />
            {state === "error" && (
                <p role="alert" className="mt-2 text-xs text-red-600 dark:text-red-400">
                    {error}
                </p>
            )}
            <p id={`${id}-note`} className="mt-2 text-xs text-slate-500 dark:text-slate-500">
                We'll only use your email to tell you when Quilt is ready.{" "}
                <a href="/privacy" className="underline underline-offset-4 hover:text-slate-700 dark:hover:text-slate-300">
                    Privacy policy
                </a>
            </p>
        </form>
    );
}

export default NotifyMeForm;
